import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant | undefined;
	size?: ButtonSize;
	loading?: boolean;
	fullWidth?: boolean;
	icon?: React.ReactNode;
	iconPosition?: 'left' | 'right';
	children?: React.ReactNode;
}

// Variant classes will be determined dynamically in the component

const sizeClasses: Record<ButtonSize, string> = {
	xs: 'px-2.5 py-1.5 text-xs min-h-[30px]',
	sm: 'px-3 py-2 text-sm min-h-[36px]',
	md: 'px-4 py-2 text-sm min-h-[38px]',
	lg: 'px-4 py-2 text-base min-h-[42px]',
	xl: 'px-6 py-3 text-base min-h-[50px]',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			size = 'md',
			loading = false,
			fullWidth = false,
			icon,
			iconPosition = 'left',
			disabled,
			className,
			children,
			...props
		},
		ref,
	) => {
		const colors = useThemeColors();
		const isDisabled = disabled === true || loading;
		const isIconOnly = icon != null && children == null;

		const getIconOnlyPadding = (size: ButtonSize): string => {
			switch (size) {
				case 'xs':
					return 'px-2 py-1.5 text-xs min-h-[30px]';
				case 'sm':
				default:
					return 'px-2.5 py-2 text-sm min-h-[36px]';
				case 'md':
					return 'px-2.5 py-2 text-sm min-h-[38px]';
				case 'lg':
					return 'px-3 py-2.5 text-base min-h-[42px]';
				case 'xl':
					return 'px-3.5 py-3 text-base min-h-[50px]';
			}
		};

		const getFocusRingVariant = (): string => {
			switch (variant) {
				case 'primary':
				case 'outline':
					return colors.classes.focusRingPrimary;
				case 'danger':
					return colors.classes.focusRingDanger;
				case 'secondary':
				case 'ghost':
				default:
					return colors.classes.focusRingDefault;
			}
		};

		const getVariantClasses = (): string => {
			switch (variant) {
				case 'primary':
					return colors.classes.primaryButton;
				case 'secondary':
					return colors.classes.secondaryButton;
				case 'outline':
					return colors.classes.outlineButton;
				case 'ghost':
					return colors.classes.ghostButton;
				case 'danger':
					return colors.classes.dangerButton;
				default:
					return '';
			}
		};

		return (
			<button
				ref={ref}
				disabled={isDisabled}
				className={cn(
					// Base styles
					'inline-flex items-center justify-center font-medium rounded-md',
					'transition-colors duration-200 touch-manipulation',

					// Focus styles
					getFocusRingVariant(),

					// Variant styles
					getVariantClasses(),

					// Force white text for primary and danger buttons - use !important
					(variant === 'primary' || variant === 'danger') && '!text-white',

					// Force white text for all children in primary/danger buttons
					(variant === 'primary' || variant === 'danger') &&
						'[&_*]:!text-white',

					// Size styles
					isIconOnly ? getIconOnlyPadding(size) : sizeClasses[size],

					// State styles
					isDisabled && 'opacity-50 cursor-not-allowed',
					fullWidth && 'w-full',

					className,
				)}
				style={{
					...(variant === 'primary' || variant === 'danger'
						? { color: 'white' }
						: {}),
					...props.style,
				}}
				{...props}
			>
				{loading ? (
					<>
						<svg
							className="animate-spin -ml-1 mr-2 h-4 w-4"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							/>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							/>
						</svg>
						Loading...
					</>
				) : (
					<>
						{icon != null && iconPosition === 'left' && (
							<span className={cn(children != null && 'mr-2 -ml-1')}>
								{typeof icon === 'object' &&
								('iconName' in icon || 'prefix' in icon) ? (
									<Icon icon={icon as IconDefinition} />
								) : (
									icon
								)}
							</span>
						)}
						{children}
						{icon != null && iconPosition === 'right' && (
							<span className={cn(children != null && 'ml-2 -mr-1')}>
								{typeof icon === 'object' &&
								('iconName' in icon || 'prefix' in icon) ? (
									<Icon icon={icon as IconDefinition} />
								) : (
									icon
								)}
							</span>
						)}
					</>
				)}
			</button>
		);
	},
);

Button.displayName = 'Button';
